/**
 * AutotestAnalytics — the "plugs and sockets" contract for the analytics
 * dashboard autotest (analytics_dashboard#index).
 *
 * The existing dashboard components read presentation values ("slots") from
 * this module's React Context instead of hardcoding them. Every slot's DEFAULT
 * equals the current production value, so the `control` arm renders identically
 * to pre-autotest. Variants live entirely in `./configs/*` (added in PR 2) and
 * register a `Partial<Surfaces>` in `ARMS` — no variant logic ever lands in the
 * dashboard's own component files.
 *
 * See autotest-onboarding/autotest/analytics_dashboard/{variants,current-state}.md
 * and ~/.claude/plans/we-are-going-to-delightful-pine.md.
 */
import React, { createContext, useContext } from 'react';
// Variant registry lives in ./configs/registry (the variant-data layer). Control
// has no entry, so it resolves to DEFAULTS.
import { ARMS } from './configs/registry';

export type ChartSeriesColors = {
  documentViews: string;
  profileViews: string;
  uniqueVisitors: string;
};

export type KpiTileTypography = {
  fontSizeClass: string;
  lineHeightClass: string;
  paddingClass: string;
  weightClass: string;
  colorClass: string;
  labelClass: string; // classes on the stat's label (below the number)
};

export type DensityMode = 'default' | 'compact' | 'spacious';

/**
 * The full slot inventory the variant slate can override. Shipped in full ahead
 * of demand (PR 1) so PR 2 variant files never need to touch central components.
 */
export type Surfaces = {
  // Overview chart (overview_tab_chart.wjs.jsx)
  chartSeriesColors: ChartSeriesColors;
  chartGridColor: string;
  // Impact KPI tiles (impact_top_stat.wjs.jsx)
  kpiTileTypography: KpiTileTypography;
  // Tab structure (index.wjs.jsx)
  defaultTab: string; // path segment, e.g. 'overview'
  tabOrder: string[] | null; // null = control order; else ordered list of tab keys
  tabLabels: Record<string, string>; // {} = control labels; else key -> label
  tabLabelClass: string; // extra class on each tab link (e.g. a larger u-fsNN)
  // Density / layout (SCSS via #AnalyticsReactRoot data-attrs + custom props)
  densityMode: DensityMode;
  panelSpacingPx: number | null; // null = control (30px)
  stickyTabNav: boolean;
  // Copy
  headerCopy: string | null; // null = control (no owner banner)
  emptyStateCopy: string | null; // null = control NoDataTab copy
  dateRangeHintCopy: string | null; // null = control (no hint)
};

export const DEFAULTS: Surfaces = {
  chartSeriesColors: {
    documentViews: '#188A1f',
    profileViews: '#0755d3',
    uniqueVisitors: '#4b4b4b',
  },
  chartGridColor: '#E9E9E9',
  kpiTileTypography: {
    fontSizeClass: 'u-fs52',
    lineHeightClass: 'u-lineHeight1',
    paddingClass: 'u-pt5x',
    weightClass: '',
    colorClass: 'u-tcGrayDarker',
    labelClass: 'u-fw500 u-tcGrayDark',
  },
  defaultTab: 'overview',
  tabOrder: null,
  tabLabels: {},
  tabLabelClass: '',
  densityMode: 'default',
  panelSpacingPx: null,
  stickyTabNav: false,
  headerCopy: null,
  emptyStateCopy: null,
  dateRangeHintCopy: null,
};

export type Arm = string;

// A variant override: any subset of slots, and for the object-valued slots any
// subset of their fields (resolveSurfaces deep-merges over DEFAULTS).
export type ArmSpec = Partial<
  Omit<Surfaces, 'chartSeriesColors' | 'kpiTileTypography' | 'tabLabels'>
> & {
  chartSeriesColors?: Partial<ChartSeriesColors>;
  kpiTileTypography?: Partial<KpiTileTypography>;
  tabLabels?: Record<string, string>;
};

/** Resolve an arm name to a full Surfaces config (deep-merging nested slots). */
export function resolveSurfaces(arm?: string | null): Surfaces {
  const overrides: ArmSpec = (arm && ARMS[arm]) || {};
  return {
    ...DEFAULTS,
    ...overrides,
    chartSeriesColors: {
      ...DEFAULTS.chartSeriesColors,
      ...(overrides.chartSeriesColors || {}),
    },
    kpiTileTypography: {
      ...DEFAULTS.kpiTileTypography,
      ...(overrides.kpiTileTypography || {}),
    },
    tabLabels: { ...DEFAULTS.tabLabels, ...(overrides.tabLabels || {}) },
  };
}

export const SurfacesContext = createContext<Surfaces>(DEFAULTS);

export const AutotestAnalyticsProvider = SurfacesContext.Provider;
export const SurfacesConsumer = SurfacesContext.Consumer;

export function useSurfaces(): Surfaces {
  return useContext(SurfacesContext);
}

/**
 * CSS custom properties for the SCSS bridge, applied to the dashboard root.
 * SCSS reads `var(--x, <current-value>)`, so control (defaults) is identical.
 */
export function rootStyleFor(s: Surfaces): React.CSSProperties {
  const style: Record<string, string> = {
    '--analytics-chart-grid': s.chartGridColor,
  };
  if (s.panelSpacingPx != null) {
    style['--analytics-panel-spacing'] = `${s.panelSpacingPx}px`;
  }
  return style as React.CSSProperties;
}

/** Data attributes for discrete layout modes (SCSS keys off these). */
export function rootDataAttrs(s: Surfaces): Record<string, string> {
  return {
    'data-autotest-density': s.densityMode,
    'data-autotest-sticky-nav': String(s.stickyTabNav),
  };
}
